import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { addBatchAssignments } from '../../../services/assignment/addBatchAssignments'
import { getBatchAssignments } from '../../../services/assignment/getBatchAssignments'
import { skylineStoryData } from '../story_data/skylineStoryData';
import { ASSIGNMENT_STATUS } from '../../../services/assignment/constants';

const initialState = {
  isModalOpen: false,
  selectedStudents: [],
  selectedStory: null,
  isViewAssignmentsModalOpen: false,
  assignments: [],
  isLoading: false,
  storyBooks: skylineStoryData.books.reduce((acc, book) => {
    acc[book.uuid] = {
      title: book.title,
      skills: book.skills,
      grade: book.grade
    };
    return acc;
  }, {})
}
const tutorAssignmentSlice = createSlice({
  name: 'tutorAssignment',
  initialState,
  reducers: {
    openModal: (state) => {
      state.isModalOpen = true
    },
    closeModal: (state) => {
      state.isModalOpen = false
      state.selectedStudents = []
    },
    openViewAssignmentsModal: (state) => {
      state.isViewAssignmentsModalOpen = true
    },
    closeViewAssignmentsModal: (state) => {
      state.isViewAssignmentsModalOpen = false
    },
    selectAllStudents: (state, action) => {
      state.selectedStudents = action.payload
    },
    clearSelectedStudents: (state) => {
      state.selectedStudents = []
    },
    clearAssignments: (state) => {
      state.assignments = []
    },
    toggleStudent: (state, action) => {
      const studentId = action.payload
      const index = state.selectedStudents.indexOf(studentId)
      if (index === -1) {
        state.selectedStudents.push(studentId)
      } else {
        state.selectedStudents.splice(index, 1)
      }
    },
    selectedStory: (state, action) => {
      state.selectedStory = action.payload
    },
    setAssignments: (state, action) => {
      state.assignments = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(assignStory.fulfilled, (state, action) => {
      state.isLoading = false;
      state.assignments = action.payload
    })
    .addCase(assignStory.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(assignStory.rejected, (state) => {
      state.isLoading = false;
    })
  }
})

export const {
  openModal,
  closeModal,
  selectAllStudents,
  clearSelectedStudents,
  toggleStudent,
  selectedStory,
  clearAssignments,
  openViewAssignmentsModal,
  closeViewAssignmentsModal,
  setAssignments,
} = tutorAssignmentSlice.actions

export const selectIsModalOpen = (state) => state.tutorAssignment.isModalOpen
export const selectSelectedStudents = (state) => state.tutorAssignment.selectedStudents
export const selectSelectedStories = (state) => state.tutorAssignment.selectedStory
export const selectIsViewAssignmentsModalOpen = (state) => state.tutorAssignment.isViewAssignmentsModalOpen
export const selectIsLoading = (state) => state.tutorAssignment.isLoading
export const selectAssignments = (state) => state.tutorAssignment.assignments
export const selectHashMapStoryBooks = (state) => state.tutorAssignment.hashMapStoryBooks
export const selectStoryBooks = (state) => state.tutorAssignment.storyBooks

export const assignStory = createAsyncThunk(
  'tutorAssignment/assignStory',
  async ({ story, selectedStudents }, { dispatch, rejectWithValue }) => {
    if (selectedStudents.length === 0) return rejectWithValue('No students selected');
    try {
      const res = await addBatchAssignments({
        entityIdList: selectedStudents,
        assignmentType: "TUTOR",
        manifest: [{ activityType: "tutor", storyId: story.uuid }],
      });
      dispatch(clearSelectedStudents());
      dispatch(clearAssignments());
      dispatch(closeModal());
      return res;
    } catch (error) {
      console.error('Error assigning story:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const viewAssignments = createAsyncThunk(
  'tutorAssignment/viewAssignments',
  async ({ studentIds, districtId, schoolId }, { dispatch, rejectWithValue }) => {
    try {
      const BATCH_SIZE = 25;
      const requests = [];

      for (let i = 0; i < studentIds.length; i += BATCH_SIZE) {
        const entityIdList = studentIds.slice(i, i + BATCH_SIZE);
        requests.push(
          getBatchAssignments({
            entityIdList,
            assignmentType: ["TUTOR"],
            districtId,
            schoolId,
            status: [
              ASSIGNMENT_STATUS.IN_PROGRESS, ASSIGNMENT_STATUS.ASSIGNED, ASSIGNMENT_STATUS.COMPLETE, ASSIGNMENT_STATUS.DONE
            ],
            limit: 1
          })
        );
      }

      const batchResults = await Promise.all(requests);

      const assignments = batchResults.flat();
      dispatch(setAssignments(assignments));
      return assignments;
    } catch (error) {
      console.error('Error fetching assignments:', error);
      return rejectWithValue(error.message);
    }
  }
);

export default tutorAssignmentSlice.reducer